/**
 * Nurse Messages — Nurse-facing conversation surface
 *
 * Allows nurses to:
 * - Create new conversations to providers (refill requests, questions, etc.)
 * - View and reply to conversations pending their action
 * - See all active conversations they're involved in
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  MessageSquare, Send, Check, Loader2, Plus, X, User,
  Pill, FlaskConical, Stethoscope, ArrowRight, Clock, FileText,
  ClipboardList, FolderOpen, CheckCircle2, Eye, Edit3
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICONS = {
  med_refill: Pill, med_question: Pill, lab_review: FlaskConical,
  referral: Stethoscope, patient_question: User, nurse_question: MessageSquare,
  general: MessageSquare,
};

export default function NurseMessages() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openConv, setOpenConv] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(null);
  const [showCompleteNote, setShowCompleteNote] = useState(false);
  const [completionNote, setCompletionNote] = useState('');
  const [markingDone, setMarkingDone] = useState(false);

  const fetchConversations = useCallback(() => {
    api.get('/conversations').then(r => setConversations(r.data.conversations || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const handleOpenConv = async (convId) => {
    try {
      const res = await api.get(`/conversations/${convId}`);
      setOpenConv(res.data.conversation);
    } catch { toast.error('Failed to load'); }
  };

  const handleReply = async () => {
    if (!openConv || !replyText.trim()) return;
    setReplying(true);
    try {
      await api.post(`/conversations/${openConv.id}/reply`, { body: replyText });
      setReplyText('');
      const res = await api.get(`/conversations/${openConv.id}`);
      setOpenConv(res.data.conversation);
      fetchConversations();
    } catch { toast.error('Reply failed'); }
    finally { setReplying(false); }
  };

  const handleMarkDone = async () => {
    if (!openConv) return;
    setMarkingDone(true);
    try {
      // If there's a pending action, complete it first
      if (openConv.action_request?.status === 'pending') {
        await api.post(`/conversations/${openConv.id}/complete-action`, {
          completion_status: 'completed', note: replyText || 'Task completed',
        });
      }
      // Resolve the conversation — this files it to the patient chart
      await api.post(`/conversations/${openConv.id}/resolve`);
      toast.success('Thread closed and filed to patient chart');
      setOpenConv(null);
      setReplyText('');
      fetchConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close');
    } finally { setMarkingDone(false); }
  };

  const pendingNurse = conversations.filter(c => c.pending_with === 'nurse');
  const pendingProvider = conversations.filter(c => c.pending_with === 'provider');

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="nurse-messages-page">
      {/* Header */}
      <div className="mx-4 mt-3 mb-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="nurse-messages-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Messages</h1>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {pendingNurse.length > 0 ? `${pendingNurse.length} need your action` : 'No pending items'}
            </p>
          </div>
          <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setShowNew(true)} data-testid="nurse-new-msg-btn">
            <Plus className="w-3.5 h-3.5" /> New Message
          </Button>
        </div>
      </div>

      {/* New message form */}
      {showNew && <NewNurseMessage onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); fetchConversations(); }} />}

      {/* Conversation lists */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
        {/* Needs Your Action */}
        {pendingNurse.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Needs Your Action ({pendingNurse.length})
            </p>
            <div className="space-y-1.5">
              {pendingNurse.map(conv => (
                <ConvCard key={conv.id} conv={conv} onOpen={() => handleOpenConv(conv.id)} isNurseView />
              ))}
            </div>
          </div>
        )}

        {/* Waiting on Provider */}
        {pendingProvider.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Waiting on Provider ({pendingProvider.length})
            </p>
            <div className="space-y-1.5">
              {pendingProvider.map(conv => (
                <ConvCard key={conv.id} conv={conv} onOpen={() => handleOpenConv(conv.id)} isNurseView />
              ))}
            </div>
          </div>
        )}

        {conversations.length === 0 && (
          <div className="text-center py-16">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">No active conversations</p>
            <p className="text-xs text-slate-400 mt-1">Use "New Message" to start one</p>
          </div>
        )}
      </div>

      {/* Floating conversation workspace */}
      {openConv && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/15" onClick={() => setOpenConv(null)} />
          <div className="relative w-full max-w-2xl max-h-[80vh] bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()} data-testid="nurse-conv-workspace">

            {/* Header with patient quick-access icons */}
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-semibold text-blue-600 shrink-0">
                  {openConv.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="min-w-0">
                  <span className="text-[12px] font-semibold text-slate-700 block truncate">{openConv.patient_name}</span>
                  <span className="text-[10px] text-slate-400 truncate block">{openConv.title}</span>
                </div>
                <Badge variant="outline" className={`text-[8px] h-4 shrink-0 ${openConv.pending_with === 'nurse' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-blue-50 text-blue-500 border-blue-200'}`}>
                  {openConv.pending_with === 'nurse' ? 'Your turn' : 'With provider'}
                </Badge>
              </div>
              {/* Patient quick-access icons */}
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => navigate(`/chart/${openConv.patient_id}?returnTo=nurse-messages&convId=${openConv.id}`)}
                  className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors group" title="Patient Chart"
                  data-testid="quick-chart-btn">
                  <ClipboardList className="w-4 h-4 text-slate-400 group-hover:text-slate-700" />
                </button>
                <button onClick={() => navigate(`/chart/${openConv.patient_id}?tab=medications&returnTo=nurse-messages&convId=${openConv.id}`)}
                  className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors group" title="Medication List"
                  data-testid="quick-meds-btn">
                  <Pill className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                </button>
                <button onClick={() => navigate(`/chart/${openConv.patient_id}?tab=documents&returnTo=nurse-messages&convId=${openConv.id}`)}
                  className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors group" title="Documents"
                  data-testid="quick-docs-btn">
                  <FolderOpen className="w-4 h-4 text-slate-400 group-hover:text-emerald-600" />
                </button>
                <div className="w-px h-5 bg-slate-200 mx-0.5" />
                <button onClick={() => setOpenConv(null)} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Document Preview Bar — shows linked document prominently */}
            {(openConv.linked_artifacts || []).filter(a => a.artifact_type === 'document').length > 0 && !viewingPdf && (
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2 shrink-0">
                <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-[11px] font-medium text-blue-800 truncate flex-1">
                  {openConv.linked_artifacts.find(a => a.artifact_type === 'document')?.label || 'Linked Document'}
                </span>
                <Button size="sm" variant="outline" className="h-6 text-[9px] gap-1 border-blue-200 text-blue-600 shrink-0"
                  onClick={async () => {
                    const docArt = openConv.linked_artifacts.find(a => a.artifact_type === 'document');
                    if (!docArt) return;
                    try {
                      const res = await api.get(`/documents/${docArt.artifact_id}/file`, { responseType: 'blob' });
                      if (res.data instanceof Blob && res.data.type === 'application/pdf') {
                        setViewingPdf({ url: URL.createObjectURL(res.data), title: docArt.label || 'Document' });
                      } else { toast.info('Original file not available'); }
                    } catch { toast.error('Failed to load document'); }
                  }} data-testid="doc-preview-view-btn">
                  <Eye className="w-3 h-3" /> View PDF
                </Button>
              </div>
            )}

            {/* Inline PDF Viewer — overlays the conversation */}
            {viewingPdf && (
              <div className="absolute inset-0 z-10 bg-white rounded-2xl flex flex-col overflow-hidden animate-in fade-in-0 duration-150">
                <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-semibold text-slate-700">{viewingPdf.title}</span>
                  </div>
                  <button onClick={() => { URL.revokeObjectURL(viewingPdf.url); setViewingPdf(null); }}
                    className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs text-slate-600 font-medium transition-colors">
                    Close PDF
                  </button>
                </div>
                <iframe src={viewingPdf.url} className="flex-1 w-full" title="Document Viewer" />
              </div>
            )}

            {/* Chat bubbles — nurse perspective (nurse on right) */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5 bg-slate-50/30">
              {(openConv.messages || []).map(msg => {
                const isNurse = msg.sender_role === 'nurse';
                return (
                  <div key={msg.id} className={`flex ${isNurse ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                      isNurse ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm shadow-sm'
                    }`}>
                      <div className={`flex items-center gap-1.5 mb-0.5 ${isNurse ? 'justify-end' : ''}`}>
                        <span className={`text-[9px] font-medium ${isNurse ? 'text-blue-200' : 'text-slate-500'}`}>{msg.sender_name}</span>
                        <span className={`text-[8px] ${isNurse ? 'text-blue-300' : 'text-slate-300'}`}>{_fmtTime(msg.timestamp)}</span>
                      </div>
                      <p className="text-[12px] leading-relaxed whitespace-pre-wrap">{msg.body}</p>
                      {(msg.attachments || []).length > 0 && (
                        <div className="space-y-1 mt-2">
                          {msg.attachments.map((att, ai) => (
                            <div key={ai} className={`rounded-lg px-2.5 py-1.5 border ${isNurse ? 'bg-white/10 border-white/20' : 'bg-slate-50 border-slate-200'}`}>
                              <div className="flex items-center gap-2">
                                <FileText className={`w-3.5 h-3.5 ${isNurse ? 'text-white/60' : 'text-slate-400'}`} />
                                <span className={`text-[10px] font-medium truncate ${isNurse ? 'text-white' : 'text-slate-700'}`}>{att.label || 'Attachment'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 7S: Action request banner + structured completion */}
            {openConv.action_request && openConv.action_request.status === 'pending' && (
              <div className="px-5 py-2.5 bg-amber-50 border-t border-amber-100 shrink-0" data-testid="action-request-banner">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-800">Action Required</span>
                </div>
                <p className="text-xs text-amber-700 mb-2">{openConv.action_request.text}</p>
                <div className="flex flex-wrap gap-1">
                  {['refill_completed', 'called_pharmacy', 'patient_notified', 'scheduled', 'printed', 'mailed'].map(status => (
                    <button key={status}
                      onClick={async () => {
                        try {
                          await api.post(`/conversations/${openConv.id}/complete-action`, {
                            completion_status: status === 'refill_completed' ? 'completed' : status === 'called_pharmacy' ? 'completed' : status,
                            note: status === 'refill_completed' ? 'Refill completed' : status === 'called_pharmacy' ? 'Called pharmacy' : '',
                          });
                          toast.success(`Marked: ${status.replace(/_/g, ' ')}`);
                          const res = await api.get(`/conversations/${openConv.id}`);
                          setOpenConv(res.data.conversation);
                          fetchConversations();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }}
                      className={`px-2.5 py-1.5 rounded-full text-[10px] font-medium transition-colors ${
                        status === 'refill_completed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        : status === 'called_pharmacy' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        : 'bg-white border border-amber-200 text-amber-700 hover:bg-amber-100'
                      }`}
                      data-testid={`complete-${status}`}>
                      {status === 'refill_completed' ? '💊 Refill Completed' :
                       status === 'called_pharmacy' ? '📞 Called Pharmacy' :
                       status.replace(/_/g, ' ')}
                    </button>
                  ))}
                  {/* Complete with Note — opens text area */}
                  <button onClick={() => setShowCompleteNote(!showCompleteNote)}
                    className={`px-2.5 py-1.5 rounded-full text-[10px] font-medium transition-colors ${
                      showCompleteNote ? 'bg-violet-200 text-violet-800' : 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                    }`} data-testid="complete-with-note">
                    📝 Complete with Note
                  </button>
                  <button onClick={async () => {
                    try {
                      await api.post(`/conversations/${openConv.id}/complete-action`, { completion_status: 'unable_to_complete', note: '' });
                      toast.success('Marked: unable to complete');
                      const res = await api.get(`/conversations/${openConv.id}`);
                      setOpenConv(res.data.conversation); fetchConversations();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                  }} className="px-2.5 py-1.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 hover:bg-red-200">
                    unable to complete
                  </button>
                </div>

                {/* Complete with Note text area */}
                {showCompleteNote && (
                  <div className="mt-2 space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
                    <textarea className="w-full text-xs border border-violet-200 rounded-lg px-3 py-2 min-h-[60px] resize-none bg-white"
                      value={completionNote} onChange={e => setCompletionNote(e.target.value)}
                      placeholder="Spoke with patient, explained EF was normal at 62%, patient had questions about medication changes, educated on Jardiance side effects, patient verbalized understanding..."
                      data-testid="completion-note-input" />
                    <Button size="sm" className="w-full h-7 text-[10px] gap-1 bg-violet-600 hover:bg-violet-700"
                      disabled={!completionNote.trim()}
                      onClick={async () => {
                        try {
                          await api.post(`/conversations/${openConv.id}/complete-action`, {
                            completion_status: 'completed', note: completionNote,
                          });
                          toast.success('Completed with note — attached to document');
                          setCompletionNote(''); setShowCompleteNote(false);
                          const res = await api.get(`/conversations/${openConv.id}`);
                          setOpenConv(res.data.conversation); fetchConversations();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }} data-testid="submit-completion-note">
                      <Edit3 className="w-3 h-3" /> Complete & Attach Note to Document
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Completed action banner */}
            {openConv.action_request && openConv.action_request.status === 'completed' && (
              <div className="px-5 py-2 bg-emerald-50 border-t border-emerald-100 flex items-center gap-2 shrink-0">
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-[10px] text-emerald-700">Action completed: <span className="font-semibold">{openConv.action_request.completion_status?.replace(/_/g, ' ')}</span></span>
              </div>
            )}

            {/* Full action bar — Reply + Mark Done & Close */}
            {openConv.status !== 'resolved' ? (
              <div className="px-5 py-3 border-t border-slate-100 bg-white shrink-0 space-y-2">
                {/* Reply row */}
                <div className="flex items-center gap-1.5 px-1">
                  <div className="w-1 h-4 rounded-full bg-blue-400" />
                  <span className="text-[10px] text-slate-500">Replying to <span className="font-semibold text-slate-700">Provider</span></span>
                </div>
                <div className="flex gap-2">
                  <Textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                    placeholder="Message provider..." className="min-h-[36px] text-sm resize-none flex-1 rounded-xl border-slate-200" />
                  <Button size="sm" onClick={handleReply} disabled={!replyText.trim() || replying} className="self-end rounded-xl bg-blue-600 hover:bg-blue-700">
                    {replying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  </Button>
                </div>
                {/* Mark Done & Close */}
                <div className="flex justify-end pt-1 border-t border-slate-100">
                  <Button size="sm" variant="outline" onClick={handleMarkDone} disabled={markingDone}
                    className="h-8 gap-1.5 text-[10px] rounded-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                    data-testid="mark-done-close-btn">
                    {markingDone ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Mark Done & Close Thread
                  </Button>
                </div>
              </div>
            ) : (
              <div className="px-5 py-3 bg-emerald-50 border-t border-emerald-100 flex items-center gap-2 shrink-0">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-xs text-emerald-700 font-medium">Completed & Filed to Chart</span>
                <span className="text-[10px] text-emerald-500 ml-auto">Viewable in patient documents</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function ConvCard({ conv, onOpen, isNurseView }) {
  const TypeIcon = TYPE_ICONS[conv.conversation_type] || MessageSquare;
  const isMyTurn = isNurseView && conv.pending_with === 'nurse';

  return (
    <button onClick={onOpen}
      className={`w-full text-left rounded-xl border bg-white p-3 hover:shadow-md transition-all ${isMyTurn ? 'border-amber-200 border-l-4 border-l-amber-400' : 'border-slate-200'}`}
      data-testid={`nurse-conv-${conv.id}`}>
      <div className="flex items-center gap-2.5">
        <div className="p-1.5 rounded-lg bg-slate-100"><TypeIcon className="w-3.5 h-3.5 text-slate-500" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-700 truncate">{conv.title}</span>
            <Badge variant="outline" className={`text-[8px] h-3.5 ${isMyTurn ? 'bg-amber-50 text-amber-600' : 'bg-slate-50 text-slate-400'}`}>
              {isMyTurn ? 'Your turn' : 'With provider'}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-slate-400">{conv.patient_name}</span>
            <span className="text-[9px] text-slate-300">{conv.message_count} msg{conv.message_count !== 1 ? 's' : ''}</span>
          </div>
          {conv.last_message_preview && (
            <p className="text-[10px] text-slate-400 truncate mt-0.5">{conv.last_message_preview}</p>
          )}
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
      </div>
    </button>
  );
}


function NewNurseMessage({ onClose, onCreated }) {
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [convType, setConvType] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (patientSearch.length < 2) return;
    api.get(`/patients?search=${patientSearch}`).then(r => setPatients(r.data.patients || [])).catch(() => {});
  }, [patientSearch]);

  // Scenario-based types with descriptions and smart placeholders
  const SCENARIOS = [
    { value: 'phone_call', label: 'Phone Call', desc: 'Patient or family called', icon: '📞',
      subjectHint: 'e.g., "Patient wants to discuss lab results"',
      bodyHint: 'Who called, what they asked about, what info you have...' },
    { value: 'med_refill', label: 'Refill Request', desc: 'Medication refill needed', icon: '💊',
      subjectHint: 'e.g., "Metformin 1000mg refill — 0 refills left"',
      bodyHint: 'Medication name, dose, refills remaining, any concerns...' },
    { value: 'lab_review', label: 'Lab Result', desc: 'Lab needs provider review', icon: '🧪',
      subjectHint: 'e.g., "A1c result 8.2% — worsening trend"',
      bodyHint: 'Which lab, key values, trend, patient concerns...' },
    { value: 'nurse_question', label: 'Clinical Question', desc: 'Need provider guidance', icon: '❓',
      subjectHint: 'e.g., "BP 160/100 — recheck or adjust meds?"',
      bodyHint: 'What you observed, what you need guidance on...' },
    { value: 'patient_question', label: 'Patient Question', desc: 'Patient has a question', icon: '🗣️',
      subjectHint: 'e.g., "Patient asks about new medication side effects"',
      bodyHint: 'What the patient asked, relevant context...' },
    { value: 'general', label: 'Other', desc: 'General message', icon: '📋',
      subjectHint: 'Brief description',
      bodyHint: 'Details for the provider...' },
  ];

  const activeScenario = SCENARIOS.find(s => s.value === convType);

  const handleSubmit = async () => {
    if (!selectedPatient || !convType || !title || !body) return;
    setSubmitting(true);
    try {
      await api.post('/conversations', {
        patient_id: selectedPatient.id,
        title,
        conversation_type: convType === 'phone_call' ? 'patient_question' : convType,
        body,
        pending_with: 'provider',
      });
      toast.success('Message sent to provider');
      onCreated();
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="mx-4 mb-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-4" data-testid="new-nurse-message">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New Message to Provider</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
      </div>

      {/* Step 1: What's happening? — scenario selection */}
      {!convType ? (
        <div>
          <p className="text-xs text-slate-500 mb-2">What's this about?</p>
          <div className="grid grid-cols-3 gap-2">
            {SCENARIOS.map(s => (
              <button key={s.value} onClick={() => setConvType(s.value)}
                className="text-left p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all group"
                data-testid={`scenario-${s.value}`}>
                <span className="text-lg block mb-1">{s.icon}</span>
                <span className="text-xs font-semibold text-slate-700 block">{s.label}</span>
                <span className="text-[10px] text-slate-400">{s.desc}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Selected scenario header */}
          <div className="flex items-center gap-2">
            <span className="text-lg">{activeScenario?.icon}</span>
            <span className="text-xs font-semibold text-slate-700">{activeScenario?.label}</span>
            <button onClick={() => setConvType('')} className="text-[9px] text-blue-500 hover:text-blue-700 ml-1">change</button>
          </div>

          {/* Step 2: Which patient? */}
          <div>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Patient</p>
            {selectedPatient ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-semibold text-blue-600">
                  {selectedPatient.name?.first?.[0]}{selectedPatient.name?.last?.[0]}
                </div>
                <span className="text-sm font-medium">{selectedPatient.name?.first} {selectedPatient.name?.last}</span>
                <Badge variant="outline" className="text-[9px] font-mono">{selectedPatient.mrn}</Badge>
                <button onClick={() => setSelectedPatient(null)} className="text-[9px] text-slate-400 hover:text-slate-600 ml-auto">change</button>
              </div>
            ) : (
              <div>
                <input type="text" placeholder="Search patient name..." value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-slate-50 placeholder:text-slate-300" autoFocus data-testid="nurse-patient-search" />
                {patients.length > 0 && (
                  <div className="border rounded-xl mt-1.5 max-h-32 overflow-y-auto shadow-sm">
                    {patients.slice(0, 5).map(p => (
                      <button key={p.id} onClick={() => { setSelectedPatient(p); setPatientSearch(''); setPatients([]); }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b last:border-0 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[9px] font-semibold text-slate-500">
                          {p.name?.first?.[0]}{p.name?.last?.[0]}
                        </div>
                        <span className="font-medium">{p.name?.first} {p.name?.last}</span>
                        <span className="text-slate-400 ml-auto">{p.mrn}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 3: Subject + Message with smart placeholders */}
          <div>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Subject</p>
            <input type="text" placeholder={activeScenario?.subjectHint || 'Brief subject'} value={title}
              onChange={e => setTitle(e.target.value)} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5" />
          </div>

          <div>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Message</p>
            <Textarea placeholder={activeScenario?.bodyHint || 'Details for the provider...'} value={body} onChange={e => setBody(e.target.value)}
              className="min-h-[80px] text-sm resize-none rounded-xl border-slate-200" />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={!selectedPatient || !convType || !title || !body || submitting}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="nurse-send-msg-btn">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send to Dr. Smith
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function _fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}
